import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location, Bundle, BundleEntry, Encounter } from 'fhir/r4';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../../shared/state.service';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { forkJoin, map, switchMap, catchError, of, throwError } from 'rxjs';

export interface LocationStatusDialogData {
  location: Location;
}

@Component({
  selector: 'app-change-location-status-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule
  ],
  templateUrl: './change-location-status-dialog.component.html',
  styleUrls: ['./change-location-status-dialog.component.scss']
})
export class ChangeLocationStatusDialogComponent implements OnInit {
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private stateService = inject(StateService);
  private fhirResourceService = inject(FhirResourceService);

  location: Location;
  newStatusControl = new FormControl<'active' | 'suspended' | 'inactive'>('active', [Validators.required]);
  newOperationalStatusControl = new FormControl<string>('', [Validators.required]);

  loading = true;
  submitting = false;
  errorMessage = '';

  childLocations: Location[] = [];
  occupiedLocations: Location[] = [];
  
  statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'inactive', label: 'Inactive' }
  ];

  operationalStatusOptions = [
    { value: 'C', label: 'Closed', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' },
    { value: 'H', label: 'Housekeeping', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' },
    { value: 'I', label: 'Isolated', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' },
    { value: 'K', label: 'Contaminated', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' },
    { value: 'O', label: 'Occupied', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' },
    { value: 'U', label: 'Unoccupied', system: 'http://terminology.hl7.org/CodeSystem/v2-0116' }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LocationStatusDialogData,
    public dialogRef: MatDialogRef<ChangeLocationStatusDialogComponent>
  ) {
    this.location = data.location;
    if (this.location.status) {
      this.newStatusControl.setValue(this.location.status as any);
    }
    if (this.location.operationalStatus?.code) {
      this.newOperationalStatusControl.setValue(this.location.operationalStatus.code);
    } else {
        // default based on status if missing
        if(this.location.status === 'active') this.newOperationalStatusControl.setValue('U');
        if(this.location.status === 'suspended') this.newOperationalStatusControl.setValue('H');
        if(this.location.status === 'inactive') this.newOperationalStatusControl.setValue('C');
    }
  }

  ngOnInit() {
    this.analyzeLocationImpact();
  }

  private analyzeLocationImpact() {
    this.loading = true;
    this.errorMessage = '';
    
    // Find all descendant locations
    const allLocs = this.stateService.orgWideResources.locations.value?.map((r: any) => r.actualResource as Location) || [];
    
    const descendants = this.getAllDescendants(this.location.id!, allLocs);
    this.childLocations = descendants;

    if (descendants.length === 0) {
      this.checkOccupancy([this.location]);
      return;
    }

    this.checkOccupancy([this.location, ...descendants]);
  }

  private getAllDescendants(parentId: string, allLocations: Location[]): Location[] {
    const children = allLocations.filter(loc => loc.partOf?.reference === `Location/${parentId}`);
    let descendants = [...children];
    for (const child of children) {
      if (child.id) {
        descendants = [...descendants, ...this.getAllDescendants(child.id, allLocations)];
      }
    }
    return descendants;
  }

  private checkOccupancy(locationsToCheck: Location[]) {
    // We check occupancy by seeing if there are any active or in-progress Encounters that have these locations
    const locationReferences = locationsToCheck.map(l => `Location/${l.id}`).join(',');
    
    if(!locationReferences) {
        this.loading = false;
        return;
    }

    const query = `${this.backendUrl}/Encounter?location=${locationReferences}&status=in-progress`;
    
    this.http.get<Bundle>(query).subscribe({
      next: (bundle) => {
        const encounters = (bundle.entry || []).map(e => e.resource as Encounter);
        
        // Find which locations are occupied
        const occupiedIds = new Set<string>();
        encounters.forEach(enc => {
          enc.location?.forEach(locRef => {
            if (locRef.location?.reference) {
              const id = locRef.location.reference.replace('Location/', '');
              occupiedIds.add(id);
            }
          });
        });

        this.occupiedLocations = locationsToCheck.filter(loc => loc.id && occupiedIds.has(loc.id));
        this.loading = false;
      },
      error: (err) => {
        console.error("Error checking location occupancy:", err);
        this.errorMessage = "Failed to verify location occupancy status. Please try again.";
        this.loading = false;
      }
    });
  }

  get canSave(): boolean {
    if (this.loading || this.submitting || !this.newStatusControl.valid || !this.newOperationalStatusControl.valid) {
      return false;
    }
    
    // If changing to inactive/suspended, block if any location is occupied
    const newStatus = this.newStatusControl.value;
    if ((newStatus === 'inactive' || newStatus === 'suspended') && this.occupiedLocations.length > 0) {
      return false;
    }
    
    return true;
  }

  save() {
    if (!this.canSave) return;
    
    this.submitting = true;
    this.errorMessage = '';

    const newStatus = this.newStatusControl.value!;
    const opStatusVal = this.newOperationalStatusControl.value!;
    const opStatusOpt = this.operationalStatusOptions.find(o => o.value === opStatusVal);

    const locationsToUpdate = [this.location, ...this.childLocations];
    
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: locationsToUpdate.map(loc => {
        const updatedLoc: Location = {
          ...loc,
          status: newStatus as any,
          operationalStatus: opStatusOpt ? {
            system: opStatusOpt.system,
            code: opStatusOpt.value,
            display: opStatusOpt.label
          } : undefined
        };
        return {
          resource: updatedLoc,
          request: {
            method: 'PUT',
            url: `Location/${loc.id}`
          }
        };
      })
    };

    this.fhirResourceService.postBundle(bundle).subscribe({
      next: () => {
        // Trigger a refresh of the locations cache
        this.http.get<Bundle>(`${this.backendUrl}/Location?_count=10000`).subscribe(locBundle => {
          this.stateService.processOrgWideBundleTransaction(locBundle);
          this.dialogRef.close(true);
        });
      },
      error: (err) => {
        console.error("Error updating location statuses:", err);
        this.errorMessage = "Failed to update location statuses. " + (err.message || '');
        this.submitting = false;
      }
    });
  }
}
